import { useCallback, useEffect, useRef, useState } from 'react';
import type { Lang, PersonWithNames } from '../domain/types';
import { displayName, initials } from '../domain/names';
import { useRepos } from '../repositories';

/**
 * `Person.portraitUrl` is either a plain URL / data URL, or `portrait:<id>`
 * pointing at a stored photo (`moment:<id>` resolves a shared Moments picture the same way). Stored photos are resolved through the person
 * repository (an object URL in the prototype, a signed URL in production) and
 * cached per id so the tree does not re-resolve on every render.
 */
const resolved = new Map<string, string>();
/**
 * How many are remembered here. This map only saves a circle from flashing its
 * initials for a frame when it mounts; the repository is what really holds the
 * urls (and lets the oldest go — see UrlCache), so there is no point in this
 * one remembering more than that does.
 */
const RESOLVED_MAX = 3000;
function remember(key: string, url: string): void {
  resolved.delete(key);
  resolved.set(key, url);
  if (resolved.size > RESOLVED_MAX) resolved.delete(resolved.keys().next().value as string);
}

export function usePortraitSrc(portraitUrl: string | undefined, small = false): string | undefined {
  return usePortrait(portraitUrl, small).src;
}

/**
 * The same thing, plus the way to recover from a url that does not work.
 *
 * A resolved url is remembered here and in the repository, which is what keeps a
 * tree of a hundred circles from re-reading a hundred photographs on every
 * render. But a remembered url can go stale — in the prototype it points into
 * the browser's own blob storage, whose backing is let go when the database
 * connection is dropped and reopened (a second tab is enough); in production a
 * signed url expires. The picture is still there; only the url is dead, and an
 * `<img>` pointed at a dead one draws the browser's broken-picture mark and
 * keeps drawing it for the rest of the session.
 *
 * So: every `<img>` in the app reports a failure, `retry` forgets the url
 * everywhere it is held and asks again, and the picture comes back. One retry
 * per element — if the second url fails too the photograph really is gone, and a
 * circle showing initials is the honest answer.
 */
/**
 * `small` asks for the little copy of a stored picture rather than the picture
 * itself. A face is kept at 640px — a megabyte and a half once decoded — and
 * nearly every place a face is drawn is a circle a tenth of that across: a row
 * on the timeline, a pin in a photograph, the tree seen whole. Four hundred
 * faces at full size is more than a phone will hold; four hundred small ones is
 * nothing. Only the places that really show a face large ask for the original.
 */
export function usePortrait(portraitUrl: string | undefined, small = false): { src: string | undefined; retry: () => void } {
  const repos = useRepos();
  const stored = portraitUrl?.startsWith('portrait:') || portraitUrl?.startsWith('moment:') ? portraitUrl : null;
  // The two sizes are two different urls, so they are remembered apart.
  const memo = stored ? (small ? `${stored}#small` : stored) : null;
  const [src, setSrc] = useState<string | undefined>(() => (memo ? resolved.get(memo) : portraitUrl));
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    if (!stored) return;
    const [kind, id] = stored.split(':', 2);
    resolved.delete(stored);
    resolved.delete(`${stored}#small`);
    if (kind === 'moment') repos.photos.forgetUrls(id);
    else repos.people.forgetPortraitUrl(id);
    setAttempt((n) => n + 1);
  }, [stored, repos]);

  useEffect(() => {
    if (!stored || !memo) {
      setSrc(portraitUrl);
      return;
    }
    /*
     * What is remembered here is shown at once — that is all it is for: a circle
     * that mounts with its face rather than a frame of initials. The repository
     * is still asked, every time. It is the one that owns the url and may have
     * let it go (its cache revokes the ones not used lately), so it has to *hear*
     * that this one is in use, and if it has made a new one since, that is the
     * one to draw. Asking costs a map lookup.
     */
    const cached = resolved.get(memo);
    if (cached) setSrc(cached);
    let cancelled = false;
    let again: ReturnType<typeof setTimeout> | undefined;
    const [kind, id] = stored.split(':', 2);
    const lookup = () =>
      kind === 'moment'
        ? (small ? repos.photos.getThumbUrl(id) : repos.photos.getUrl(id))
        : (small ? repos.people.getPortraitThumbUrl(id) : repos.people.getPortraitUrl(id));
    // A stored photo that fails to resolve once (storage still opening, a hiccup reading the
    // blob) is looked up again shortly after, rather than staying blank until the next visit.
    const run = (tries: number) => {
      void lookup()
        .catch(() => undefined)
        .then((url) => {
          if (cancelled) return;
          if (url) {
            remember(memo, url);
            setSrc(url);
          } else if (cached) {
            // Nothing came back but there is still something on the circle: leave it; a dead url says so itself (onError).
          } else if (tries < 2) {
            again = setTimeout(() => run(tries + 1), 600 * (tries + 1));
          } else {
            setSrc(undefined);
          }
        });
    };
    run(0);
    return () => {
      cancelled = true;
      if (again) clearTimeout(again);
    };
    // `attempt` is what a failed <img> bumps to make this look the picture up again.
  }, [stored, memo, small, portraitUrl, repos, attempt]);

  return { src, retry };
}

/**
 * Everything an `<img>` drawn from a stored picture needs: what to show, and
 * what to do when it will not load — forget the url everywhere and ask again,
 * once per failure rather than once per element, so a picture that healed and
 * later goes stale again (a url let go by the cache, a signed url expiring in
 * production) heals again.
 */
export function usePortraitImg(portraitUrl: string | undefined, small = false): { src: string | undefined; onError: () => void; onLoad: () => void } {
  const { src, retry } = usePortrait(portraitUrl, small);
  const healed = useRef(false);
  const onError = useCallback(() => {
    if (healed.current) return;
    healed.current = true;
    retry();
  }, [retry]);
  const onLoad = useCallback(() => {
    healed.current = false;
  }, []);
  return { src, onError, onLoad };
}

export function Avatar({
  person,
  lang,
  size = 'md',
  className = '',
  full,
}: {
  person: PersonWithNames;
  lang: Lang;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'tree';
  className?: string;
  /**
   * Draw the face from the original rather than its small copy. The big sizes
   * do anyway; the tree asks for it once somebody has zoomed right in on a
   * circle, which is the one time a small size is looked at closely.
   */
  full?: boolean;
}) {
  const hue = `var(--hue-${person.portraitHue % 16})`;
  const deceased = person.status === 'deceased';
  /* One second chance per failure: see the note on usePortrait. */
  const { src, onError, onLoad } = usePortraitImg(person.portraitUrl, !(full ?? (size === 'lg' || size === 'xl')));
  return (
    <span
      className={`avatar avatar-${size} ${deceased ? 'deceased' : ''} ${src ? 'has-photo' : ''} ${className}`}
      style={{ background: src ? undefined : hue }}
      aria-hidden="true"
    >
      {src ? (
        <img
          src={src}
          alt=""
          draggable={false}
          decoding="async"
          onError={onError}
          onLoad={onLoad}
        />
      ) : (
        initials(person, lang)
      )}
    </span>
  );
}

/** Name rendered with bidi isolation so mixed-script names stay readable (section 14.2). */
export function PersonName({ person, lang, className }: { person: PersonWithNames; lang: Lang; className?: string }) {
  const name = displayName(person, lang);
  const nameIsHebrew = /[֐-׿]/.test(name);
  return (
    <bdi className={className} dir={nameIsHebrew ? 'rtl' : 'ltr'}>
      {name}
    </bdi>
  );
}
